import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { formatError } from "../lib/api";
import { Brand } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight, ShieldCheck, Sparkles, GitBranch, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const DEMO = [
  { role: "Admin", email: "admin@profecia.io", password: "Admin@123" },
  { role: "Analyst", email: "analyst@profecia.io", password: "Analyst@123" },
  { role: "Viewer", email: "viewer@profecia.io", password: "Viewer@123" },
  { role: "Connector Admin", email: "connector@profecia.io", password: "Connector@123" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@profecia.io");
  const [password, setPassword] = useState("Admin@123");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(u.role === "super_admin" ? "/app/super-admin" : "/app");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen pm-os-bg flex items-center justify-center p-4">
      <button onClick={() => navigate("/")} data-testid="login-back-home"
        className="fixed top-5 left-5 z-50 flex items-center gap-2 glass rounded-full px-4 h-10 text-sm font-medium text-[#0f2547] hover:bg-white/80 pm-transition">
        <ArrowLeft className="w-4 h-4" /> Back to Main
      </button>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-5xl glass-strong rounded-[36px] overflow-hidden grid md:grid-cols-2 shadow-2xl">
        {/* Left brand panel */}
        <div className="pm-gradient-dark p-11 text-white relative overflow-hidden hidden md:flex flex-col justify-between">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#0e7aa6]/30 blur-3xl" />
          <div className="absolute -left-10 bottom-10 w-52 h-52 rounded-full bg-[#16a9c7]/20 blur-3xl" />
          <div className="relative z-10">
            <div className="mb-12"><Brand size="lg" dark tagline /></div>
            <h1 className="font-display text-5xl font-semibold leading-[1.05]">The AI-native process intelligence platform</h1>
            <p className="text-indigo-100/75 text-sm leading-relaxed max-w-sm mt-5 font-body">
              Discover, analyse and optimise any business process. Zero-config ingestion, semantic correlation and LLM-powered root-cause analysis — across ERP, CRM, ITSM and beyond.
            </p>
          </div>
          <div className="relative z-10 space-y-3 mt-8">
            {[[GitBranch, "Auto-discover process maps from raw event logs"],
              [Sparkles, "Natural-language querying with narrated insights"],
              [ShieldCheck, "Multi-tenant, RBAC & enterprise-grade security"]].map(([Icon, t], i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-indigo-50/90 font-body">
                <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center"><Icon className="w-4 h-4" /></div>{t}
              </div>
            ))}
          </div>
        </div>
        {/* Right form */}
        <div className="p-9 md:p-11 flex flex-col justify-center bg-white/60">
          <div className="md:hidden mb-6"><Brand size="md" /></div>
          <h2 className="font-display text-4xl font-semibold text-[#0f2547]">Welcome back</h2>
          <p className="text-sm text-slate-500 mb-7 font-body">Access your process intelligence workspace.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-slate-600 font-body">Work email</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-white border-slate-200 h-12 rounded-2xl" required />
            </div>
            <div>
              <Label className="text-slate-600 font-body">Password</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-white border-slate-200 h-12 rounded-2xl" required />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-12 rounded-full pm-gradient text-white font-semibold pm-transition hover:opacity-90 border-0 shadow-lg shadow-sky-900/25">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </form>
          <div className="mt-7">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 mb-2.5">Quick demo access</div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button key={d.email} data-testid={`demo-${d.role.replace(/\s/g, "-").toLowerCase()}`} onClick={() => { setEmail(d.email); setPassword(d.password); }}
                  className="text-left px-3 py-2.5 rounded-2xl neu hover:-translate-y-0.5 pm-transition">
                  <div className="text-xs font-semibold text-[#0f2547]">{d.role}</div>
                  <div className="text-[11px] text-slate-500 truncate">{d.email}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
